import { useCallback, useEffect, useMemo, useState } from "react";

import { useParams } from "react-router";
import { Metadata } from "@doitintl/cmp-models";
import { type AttributionFilter, type Positions } from "@doitintl/cmp-models";
import { Add } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Alert, Button, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid2";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";

import { allocationTxt } from "../../../../../assets/texts/CloudAnalytics/allocation";
import { ExpandMore } from "../../../../../Components/ExpendMore";
import { useAttributionGroups } from "../../../../../Components/hooks/cloudAnalytics/attributionGroups/useAttributionGroups";
import { useAuthContext } from "../../../../../Context/AuthContext";
import { type AttributionWRef, type MetadataOption } from "../../../../../types";
import useSegmentTrackEvent from "../../../../../utils/useSegmentTrackEvent";
import { type KeyTypeValues } from "../../../api";
import { useHasAllocationWritePermission } from "../../../hooks/useHasAllocationWritePermission";
import { isEditor } from "../../../utilities";
import { RuleDivider } from "../../components/RuleDivider";
import useAllocationDndStore from "../../hooks/useAllocationDnd";
import { formStoreManager } from "../../hooks/useAllocationFormState";
import useDimensionsSelection from "../../hooks/useDimensionsSelection";
import { trackAllocationEvent } from "../../utils";
import { AllocationDraggableHolder } from "../AllocationDraggableHolder";
import ActionButtons from "./ActionButtons";
import { AllocationCalculation } from "./AllocationCalculation/AllocationCalculation";
import AllocationFormulaField from "./AllocationFormulaField";
import AllocationNameField from "./AllocationNameField";
import type ReportData from "../../../ReportData";

const CONTENT_PADDING_LEFT = "46px";
const NOT_ALLOWED_CHARS_REGEX = /[^a-zA-Z0-9-_.,:()[\]%\s]/g;

type Props = {
  allocationRuleIndex?: number;
  grabbing?: boolean;
  initialAttributionID: string;
  dimensions: MetadataOption[];
  disabled?: boolean;
  queryRunning?: boolean;
  queryResults?: ReportData | null;
  handleDimensionLabelsChange: (
    values: KeyTypeValues[],
    filter?: AttributionFilter,
    position?: Positions
  ) => Promise<MetadataOption | null>;
};

export function AllocationDraggableItem({
  grabbing,
  initialAttributionID,
  allocationRuleIndex,
  dimensions,
  disabled = false,
  handleDimensionLabelsChange,
  queryRunning = false,
  queryResults = null,
}: Props) {
  const { currentUser } = useAuthContext({ mustHaveUser: true });
  const { allocationId } = useParams<{ allocationId: string }>();
  const { removeItem, addItem, items, hasUnallocated, isAllocationEditor, isGroup, insertItem } =
    useAllocationDndStore();
  const [expanded, setExpanded] = useState(true);
  const [attributionGroups] = useAttributionGroups();

  const canEdit = useHasAllocationWritePermission(isAllocationEditor);

  const isFirst = allocationRuleIndex === 0;
  const { trackEvent } = useSegmentTrackEvent();
  const formStore = formStoreManager.getStore(initialAttributionID);

  const {
    values,
    setName,
    setFilterFields,
    setViewType,
    setCopyRule,
    setFormula,
    setEditWarnings,
    viewType,
    setSelectedExistingRule,
    selectedExistingRule,
    isEditMode,
    resetForm,
    setIsDirty,
    isFieldsDirty,
  } = formStore();

  const usedInAttributionGroup = useMemo(
    () =>
      attributionGroups.filter(
        (group) =>
          group.ref.id !== allocationId &&
          group.data?.attributions?.some((attribution) => attribution.id === selectedExistingRule?.ref.id)
      ),
    [allocationId, attributionGroups, selectedExistingRule]
  );

  const isRuleEditor = useMemo(
    () =>
      selectedExistingRule &&
      isEditor(currentUser.email, {
        collaborators: selectedExistingRule?.data?.collaborators || [],
      }),
    [currentUser.email, selectedExistingRule]
  );

  const canRemove = useMemo(() => items.main?.length > 1, [items.main]);
  const showEditWarning = !isGroup && isEditMode && isFirst && (items.main.length > 1 || hasUnallocated);
  const showEditRuleWarning = isFieldsDirty && viewType !== "new" && isEditMode;
  const isSingleRule = useMemo(() => items.main?.length === 1 && isEditMode, [items.main, isEditMode]);
  const canDuplicate = useMemo(
    () => (!!values.name && !!values.formula && !!values.filterFields?.length) || !!selectedExistingRule,
    [selectedExistingRule, values.filterFields?.length, values.formula, values.name]
  );

  const filteredDimensions = useMemo(() => {
    if (!dimensions?.length) return [];

    const filtered = dimensions.slice();

    return filtered.filter((d) => {
      if (d.data.type === Metadata.DATETIME) {
        d._visible = false;
        return true;
      }
      return d.data.type !== Metadata.ATTRIBUTION;
    });
  }, [dimensions]);

  const { DimensionsSelection, filters, setFilters, setDisabled } = useDimensionsSelection({
    isEditMode: isEditMode && viewType !== "new",
    initialFilters: values.filterFields ?? [],
    dimensions: filteredDimensions,
    excludeSelectMetadataIds: undefined,
    handleDimensionLabelsChange,
  });

  useEffect(() => {
    setDisabled(disabled);
  }, [disabled, setDisabled]);

  const handleExpandClick = useCallback((e) => {
    e.preventDefault();
    setExpanded((prev) => !prev);
  }, []);

  const onRemoveRule = useCallback(() => {
    trackAllocationEvent({
      trackEvent,
      eventName: "Allocation Rule Deleted",
      ruleCount: items.main?.length || 0,
      hasUnallocated,
      allocationId,
      currentUserEmail: currentUser.email,
      collaborators: selectedExistingRule?.data.collaborators,
    });
    removeItem(initialAttributionID);
    formStoreManager.removeStore(initialAttributionID);
  }, [
    selectedExistingRule,
    initialAttributionID,
    trackEvent,
    allocationId,
    items.main?.length,
    hasUnallocated,
    currentUser.email,
    removeItem,
  ]);

  const handleAllocationNameChanged = useCallback(
    (event) => {
      const value = event.target.value;
      const sanitizedValue = value.replace(NOT_ALLOWED_CHARS_REGEX, "");
      setName(sanitizedValue);
    },
    [setName]
  );

  const handleSelectExistingAllocation = useCallback(
    (value: AttributionWRef | null) => {
      if (!value) {
        setSelectedExistingRule(null);

        resetForm();
        setFilters([]);
        return;
      }

      const disabled = !isEditor(currentUser.email, { collaborators: value?.data?.collaborators || [] }) || !canEdit;
      setName(value?.data.name);
      setSelectedExistingRule(value);
      setFilters(value?.data.filters ?? []);
      setFormula(value?.data.formula ?? "");
      setDisabled(disabled);
    },
    [resetForm, setDisabled, setFilters, setFormula, setName, setSelectedExistingRule, currentUser.email, canEdit]
  );

  const handleToggleSource = useCallback(
    (event, value) => {
      if (!value) return null;

      setViewType(value);

      if (value === "new") {
        setName("");
        setFilters([]);
        setIsDirty(false);
        setDisabled(false);
      } else {
        setName(selectedExistingRule?.data.name ?? "");
        setFilters(selectedExistingRule?.data.filters ?? []);
        setIsDirty(false);

        setDisabled(false);
      }
    },
    [
      selectedExistingRule?.data.filters,
      selectedExistingRule?.data.name,
      setDisabled,
      setFilters,
      setIsDirty,
      setName,
      setViewType,
    ]
  );

  const handleSaveACopy = useCallback(() => {
    setViewType("new");
    setCopyRule(selectedExistingRule!);
    setFormula(selectedExistingRule?.data.formula ?? "");
    setDisabled(false);
    setIsDirty(true);
  }, [selectedExistingRule, setCopyRule, setDisabled, setFormula, setIsDirty, setViewType]);

  const onDuplicateRule = useCallback(() => {
    if ((!values.name && viewType === "new") || (!selectedExistingRule && viewType !== "new")) return null;

    const newStoreId = crypto.randomUUID();
    const allocation = {
      name: `${values.name || selectedExistingRule?.data.name} (Copy)`,
      filterFields: values.filterFields,
      formula: values.formula,
    };

    trackAllocationEvent({
      trackEvent,
      eventName: "Allocation Rule Copied",
      ruleCount: (items.main?.length || 0) + 1,
      hasUnallocated,
      allocationId,
      currentUserEmail: currentUser.email,
      collaborators: selectedExistingRule?.data.collaborators,
    });

    const newStore = formStoreManager.getStore(newStoreId);
    newStore.setState((state) => ({
      ...state,
      values: allocation,
      viewType: "new",
    }));
    addItem(newStoreId);
  }, [
    values.name,
    values.filterFields,
    values.formula,
    viewType,
    selectedExistingRule,
    trackEvent,
    items.main?.length,
    hasUnallocated,
    allocationId,
    currentUser.email,
    addItem,
  ]);

  useEffect(() => {
    setFilterFields(filters);
  }, [filters, setFilterFields]);

  useEffect(() => {
    if (selectedExistingRule && viewType === "existing") {
      handleSelectExistingAllocation(selectedExistingRule);
    }
  }, [handleSelectExistingAllocation, selectedExistingRule, viewType]);

  const viewSelector = useMemo(
    () => (
      <Stack
        direction="row"
        spacing={2}
        sx={{
          display: { xs: "none", sm: "none", md: "flex" },
          justifyContent: "space-between",
          pl: CONTENT_PADDING_LEFT,
        }}
      >
        <FormControl>
          <RadioGroup row value={viewType} onChange={handleToggleSource}>
            <FormControlLabel
              value="new"
              control={<Radio />}
              label={allocationTxt.CREATE_NEW}
              disabled={isSingleRule || disabled || !canEdit}
            />
            <FormControlLabel
              value="existing"
              control={<Radio />}
              label={allocationTxt.USE_EXISTING_ALLOCATION}
              disabled={isSingleRule || disabled || !canEdit}
            />
          </RadioGroup>
        </FormControl>

        <ActionButtons
          canDuplicate={canDuplicate}
          canRemove={canRemove}
          onDuplicateRule={onDuplicateRule}
          onRemoveRule={onRemoveRule}
        />
      </Stack>
    ),
    [
      viewType,
      handleToggleSource,
      isSingleRule,
      disabled,
      canEdit,
      canDuplicate,
      canRemove,
      onDuplicateRule,
      onRemoveRule,
    ]
  );

  const handleInsertAllocation = useCallback(() => {
    insertItem(
      crypto.randomUUID(),
      items.main.findIndex((item) => item === initialAttributionID)
    );
  }, [initialAttributionID, insertItem, items.main]);

  const expandableHeader = useMemo(
    () => (
      <Stack direction="row" gap={1} sx={{ mt: 2, width: "100%" }}>
        <Grid>
          <ExpandMore expand={expanded} onClick={handleExpandClick} aria-expanded={expanded} aria-label="show more">
            <ExpandMoreIcon />
          </ExpandMore>
        </Grid>

        <Grid width="100%">
          <AllocationNameField
            viewType={viewType}
            isEditMode={isEditMode}
            isSingleRule={isSingleRule}
            values={values}
            selectedExistingRule={selectedExistingRule}
            onAllocationNameChanged={handleAllocationNameChanged}
            onSelectExistingAllocation={handleSelectExistingAllocation}
            disabled={(Boolean(selectedExistingRule?.data?.collaborators) && !isRuleEditor) || disabled}
          />
        </Grid>
      </Stack>
    ),
    [
      expanded,
      handleExpandClick,
      viewType,
      isEditMode,
      isSingleRule,
      values,
      selectedExistingRule,
      handleAllocationNameChanged,
      handleSelectExistingAllocation,
      isRuleEditor,
      disabled,
    ]
  );

  useEffect(() => {
    if (isFieldsDirty && usedInAttributionGroup.length) {
      setEditWarnings(usedInAttributionGroup.map((group) => group.data.name).join(", "));
    } else {
      setEditWarnings("");
    }
  }, [isFieldsDirty, usedInAttributionGroup, setEditWarnings]);

  return (
    <>
      <Stack>
        {!grabbing && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={isFirst ? "flex-end" : "space-between"}
            sx={{ position: "relative", height: "78px" }}
          >
            <>
              {!isFirst && (
                <Stack>
                  <RuleDivider />
                  <Box sx={{ ml: 7 }}>
                    <Typography variant="body1">{allocationTxt.ALLOCATION_MIDDLE_SEPARATOR}</Typography>
                  </Box>
                </Stack>
              )}

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  size="medium"
                  disabled={disabled || !canEdit}
                  startIcon={<Add />}
                  onClick={handleInsertAllocation}
                >
                  {allocationTxt.ADD_ALLOCATION}
                </Button>
              </Box>
            </>
          </Stack>
        )}
        <AllocationDraggableHolder
          grabbing={grabbing}
          height="auto"
          disabled={!canRemove || disabled || !canEdit}
          id={initialAttributionID}
          key={initialAttributionID}
          alignItems="flex-start"
        >
          <Box sx={{ p: 1, pb: 2, ml: { xs: 0, sm: 0, md: -5 } }}>
            {viewSelector}
            {expandableHeader}
            {!isRuleEditor && isAllocationEditor && viewType === "existing" && selectedExistingRule && (
              <Alert
                severity="info"
                sx={{
                  mt: 1,
                  ml: CONTENT_PADDING_LEFT,
                  maxWidth: "540px",
                }}
                action={
                  <Button color="inherit" size="small" onClick={handleSaveACopy} disabled={disabled}>
                    {allocationTxt.ALLOCATION_COPY}
                  </Button>
                }
              >
                {allocationTxt.ALLOCATION_RULE_INFO}
              </Alert>
            )}
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              {(viewType === "new" || (viewType === "existing" && selectedExistingRule)) && (
                <Box sx={{ pl: CONTENT_PADDING_LEFT, mt: 2 }}>
                  {DimensionsSelection}
                  {!!filters.length && (
                    <AllocationFormulaField
                      formStore={formStore}
                      disabled={(!isRuleEditor && !!selectedExistingRule) || disabled}
                    />
                  )}
                </Box>
              )}
            </Collapse>
            <Box sx={{ marginX: 5, mt: 2, mb: 1, maxWidth: { lg: "546px", md: "100%" } }}>
              <AllocationCalculation
                queryRunning={queryRunning}
                queryResults={queryResults}
                resourceName={values.name}
              />
            </Box>
            {showEditRuleWarning && (
              <Alert severity="warning" sx={{ marginX: 5, mt: 2, mb: 1, maxWidth: { lg: "546px", md: "100%" } }}>
                {allocationTxt.ALLOCATION_EDIT_WARNING}
              </Alert>
            )}
          </Box>
        </AllocationDraggableHolder>
        {showEditWarning && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {allocationTxt.SINGLE_ALLOCATION_EDIT_WARNING}
          </Alert>
        )}
      </Stack>
    </>
  );
}
